"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

/**
 * Home-page nudge back into the editor.
 *
 * The editor is "ephemeral": a project is created on /import and only removed on
 * an explicit Discard (or purged on the next import). So if the user closed the
 * editor tab without discarding, the recording is still sitting in IndexedDB +
 * OPFS. On the home page we surface that leftover session as a toast so they can
 * jump back in.
 *
 * It reads the projects IndexedDB directly (keeping the heavy EditorCore out of
 * the marketing bundle) and shows nothing unless a resumable project exists.
 */

// Mirror of the storage service config (src/services/storage/service.ts).
const PROJECTS_DB = "video-editor-projects";
const PROJECTS_STORE = "projects";

type LeftoverProject = { id: string; name: string };

async function findMostRecentProject(): Promise<LeftoverProject | null> {
	if (typeof indexedDB === "undefined") return null;

	// Avoid creating the DB from the marketing page: only open it if it already
	// exists. indexedDB.databases() is available in Chromium (our target).
	try {
		if (indexedDB.databases) {
			const dbs = await indexedDB.databases();
			if (!dbs.some((d) => d.name === PROJECTS_DB)) return null;
		}
	} catch {
		/* fall through and try opening */
	}

	const db = await new Promise<IDBDatabase | null>((resolve) => {
		const request = indexedDB.open(PROJECTS_DB);
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => resolve(null);
		// If the store doesn't exist yet, this is a fresh DB with nothing to resume.
		request.onupgradeneeded = () => resolve(null);
	});
	if (!db) return null;

	try {
		if (!db.objectStoreNames.contains(PROJECTS_STORE)) return null;

		const records = await new Promise<Array<Record<string, unknown>>>(
			(resolve, reject) => {
				const tx = db.transaction([PROJECTS_STORE], "readonly");
				const req = tx.objectStore(PROJECTS_STORE).getAll();
				req.onsuccess = () => resolve(req.result ?? []);
				req.onerror = () => reject(req.error);
			},
		);

		let best: LeftoverProject | null = null;
		let bestTime = -Infinity;
		for (const record of records) {
			const metadata = record?.metadata as
				| { id?: string; name?: string; updatedAt?: string }
				| undefined;
			if (!metadata?.id) continue;
			const time = metadata.updatedAt ? Date.parse(metadata.updatedAt) : 0;
			if (time >= bestTime) {
				bestTime = time;
				best = { id: metadata.id, name: metadata.name || "Untitled project" };
			}
		}
		return best;
	} finally {
		db.close();
	}
}

/**
 * Renders no UI of its own — it just fires a sonner toast (mounted in
 * src/app/layout.tsx) when a resumable editing session is found.
 */
export function ResumeEditingToast() {
	const router = useRouter();
	const firedRef = useRef(false);

	useEffect(() => {
		let cancelled = false;
		findMostRecentProject()
			.then((project) => {
				if (cancelled || !project || firedRef.current) return;
				firedRef.current = true;

				// Persist until the user acts (resume) or dismisses it. The global
				// Toaster has no close button, so enable one for this toast.
				toast("You have an unsaved editing session", {
					description: `“${project.name}” is still here. Pick up where you left off.`,
					duration: Infinity,
					closeButton: true,
					action: {
						label: "Resume editing",
						onClick: () => router.push(`/editor/${project.id}`),
					},
				});
			})
			.catch(() => {
				/* no resumable session */
			});
		return () => {
			cancelled = true;
		};
	}, [router]);

	return null;
}
